import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertCanManageItem } from "./menu-shared";

export const getPublicMenusForBusiness = createServerFn({ method: "GET" })
  .inputValidator((d: { businessId: string }) => d)
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const supabase = createClient(process.env.SUPABASE_URL!, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input: any, init: any) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data: menus, error } = await supabase.from("menus")
      .select("id, slug, public_name, internal_name, menu_type, default_language, available_languages, currency_code, valid_from, valid_to, version, cover_image_path")
      .eq("business_id", data.businessId)
      .eq("status", "published")
      .is("archived_at", null)
      .order("sort_order");
    if (error) throw new Error(error.message);
    return menus ?? [];
  });

export const getPublicMenu = createServerFn({ method: "GET" })
  .inputValidator((d: { businessId: string; menuSlug: string }) => d)
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const supabase = createClient(process.env.SUPABASE_URL!, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input: any, init: any) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data: menu } = await supabase.from("menus")
      .select("id, business_id, slug, public_name, internal_name, menu_type, default_language, available_languages, currency_code, valid_from, valid_to, version, cover_image_path, include_images_in_pdf")
      .eq("business_id", data.businessId).eq("slug", data.menuSlug).eq("status", "published").is("archived_at", null).maybeSingle();
    if (!menu) return null;
    const [{ data: trs }, { data: cats }, { data: catTrs }, { data: items }, { data: itemTrs }, { data: itemAll }, { data: allergens }, { data: allergenTrs }] = await Promise.all([
      supabase.from("menu_translations").select("*").eq("menu_id", menu.id),
      supabase.from("menu_categories").select("*").eq("menu_id", menu.id).eq("is_active", true).order("sort_order"),
      supabase.from("menu_category_translations").select("*, menu_categories!inner(menu_id)").eq("menu_categories.menu_id", menu.id),
      supabase.from("menu_items").select("id, category_id, price, original_price, portion_size, image_path, image_alt, image_focus, availability_status, is_vegetarian, is_vegan, is_gluten_free, is_lactose_free, sort_order, menu_categories!inner(menu_id)").eq("menu_categories.menu_id", menu.id).eq("is_active", true).neq("availability_status", "hidden").order("sort_order"),
      supabase.from("menu_item_translations").select("*, menu_items!inner(category_id, menu_categories!inner(menu_id))").eq("menu_items.menu_categories.menu_id", menu.id),
      supabase.from("menu_item_allergens").select("*, menu_items!inner(category_id, menu_categories!inner(menu_id))").eq("menu_items.menu_categories.menu_id", menu.id),
      supabase.from("allergens").select("*").order("sort_order"),
      supabase.from("allergen_translations").select("*"),
    ]);
    return {
      menu, translations: trs ?? [], categories: cats ?? [], categoryTranslations: catTrs ?? [],
      items: items ?? [], itemTranslations: itemTrs ?? [], itemAllergens: itemAll ?? [],
      allergens: allergens ?? [], allergenTranslations: allergenTrs ?? [],
    };
  });

export const createMenuImageSignedUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { itemId: string; ext: string }) => d)
  .handler(async ({ data, context }) => {
    const ext = data.ext.replace(/[^a-z0-9]/gi, "").toLowerCase().slice(0, 5) || "jpg";
    if (!["jpg", "jpeg", "png"].includes(ext)) throw new Error("Povoleny jsou pouze soubory JPEG nebo PNG.");
    const bid = await assertCanManageItem(context.supabase, context.userId, data.itemId);
    const path = `${bid}/${data.itemId}/${crypto.randomUUID()}.${ext}`;
    const { data: signed, error } = await (context.supabase as any).storage.from("menu-images").createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

export const getMenuImageSignedUrl = createServerFn({ method: "GET" })
  .inputValidator((d: { path: string }) => {
    if (typeof d?.path !== "string" || d.path.length === 0 || d.path.length > 512) {
      throw new Error("Invalid path.");
    }
    // Path must match `${businessId}/${itemId}/${filename}` — no traversal.
    if (!/^[a-f0-9-]{36}\/[a-f0-9-]{36}\/[A-Za-z0-9._-]+$/.test(d.path)) {
      throw new Error("Invalid path.");
    }
    return d;
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [pathBusinessId, pathItemId] = data.path.split("/");

    // Authorize: allow anonymous access ONLY when the image belongs to an item in a
    // currently published, non-archived menu of the business that owns the path
    // prefix. Everything else mirrors the storage RLS policy: the caller must be
    // authenticated and able to manage that exact business.
    const { data: item } = await supabaseAdmin
      .from("menu_items")
      .select("id, menu_categories!inner(menus!inner(status, archived_at, business_id))")
      .eq("image_path", data.path)
      .maybeSingle();

    const menu = (item as any)?.menu_categories?.menus;
    const itemMatchesPath =
      !!item &&
      (item as any).id === pathItemId &&
      menu?.business_id === pathBusinessId;
    const isPublished =
      itemMatchesPath && menu.status === "published" && menu.archived_at === null;

    if (!isPublished) {
      // Fall back to authenticated ownership check against the business that owns
      // the storage path — not just the item id embedded in it.
      const { getRequestHeader } = await import("@tanstack/react-start/server");
      const authHeader = getRequestHeader("authorization") || getRequestHeader("Authorization");
      const token = authHeader?.replace(/^Bearer\s+/i, "");
      if (!token) throw new Error("Unauthorized");

      const { createClient } = await import("@supabase/supabase-js");
      const url = process.env.SUPABASE_URL!;
      const anon = process.env.SUPABASE_PUBLISHABLE_KEY!;
      const userClient = createClient(url, anon, {
        auth: { persistSession: false, autoRefreshToken: false },
        global: { headers: { Authorization: `Bearer ${token}` } },
      });
      const { data: u } = await userClient.auth.getUser();
      if (!u?.user?.id) throw new Error("Unauthorized");

      const { data: canManage, error: manageErr } = await userClient.rpc("can_manage_business", {
        _uid: u.user.id,
        _bid: pathBusinessId,
      });
      if (manageErr) throw new Error("Forbidden");
      if (!canManage) throw new Error("Forbidden");

      // When the path is attached to a menu item, re-check ownership of that item
      // too, so a manager cannot read another business' object through its own prefix.
      if (item) {
        if (!itemMatchesPath) throw new Error("Forbidden");
        await assertCanManageItem(userClient, u.user.id, pathItemId);
      }
    }

    const { data: url } = await supabaseAdmin.storage
      .from("menu-images")
      .createSignedUrl(data.path, 3600);
    return url?.signedUrl ?? null;
  });
